// Ordnance on screen: flying grenades, rockets, harpoons, firework shells and seekers, the Big
// One's laser, ordnance crates, sentries, and canopy domes. Placeholder art in the kit palette:
// the shapes and colours are what matter until the real models land. Allocation-free per frame
// apart from spawns.

import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { TICK_RATE } from '../../sim/core';
import { ENEMIES } from '../../sim/defs';
import { BEACON_SECS, LAUNCH_SPEED, OKind, ORD, SEEKER_SPEED, ordPosAt } from '../../sim/arsenal';
import { DUEL_BOLT_SPEED, Canopy, GRENADES, Payload, W, WEAPONS, weaponDef } from '../../sim/weapons';
import type { NetEnemy } from '../../sim/protocol';
import type { World } from '../../sim/world';
import { PAL } from '../render/palette';
import { facet, outlineMaterial, paint, printMaterial, unlitMaterial } from '../render/materials';
import { El, type Fx } from '../render/fx/index';
import { ELEM_COLOR, buildOrdnanceMesh, buildWeaponMesh } from './guns';

interface Flying {
  id: number; kind: number; sub: number; owner: number; tick0: number; target: number;
  ox: number; oy: number; oz: number; vx: number; vy: number; vz: number;
  x: number; y: number; z: number;
  mesh: THREE.Object3D;
  shownAt: number; catchup: number;
  /** spawned locally on the throw; waiting to be adopted by the server's OrdSpawn */
  predicted: boolean;
  dead: boolean;
  age: number;
  /** fused grenades: settled (or stuck to robot `stick`, at offset sx sy sz) waiting on `fuseEnd` */
  rest: boolean; stick: number; sx: number; sy: number; sz: number; fuseEnd: number;
  /** homing kinds integrate on the client (server sends the end) */
  hx: number; hy: number; hz: number; hvx: number; hvy: number; hvz: number;
}

const ELEM_FX: Record<number, number> = { 0: El.Fire, 1: El.Acid, 2: El.Shock, 3: El.Ice, 8: El.Shock };
const CANOPY_FX: Record<number, number> = {
  [Canopy.Fire]: El.Fire, [Canopy.Ice]: El.Ice, [Canopy.Shock]: El.Shock, [Canopy.Acid]: El.Acid,
  [Canopy.Magnetic]: -1, [Canopy.Aegis]: -1, [Canopy.Coolant]: El.Water, [Canopy.Mending]: -1,
};
const CANOPY_COLOR: Record<number, number> = {
  [Canopy.Fire]: 0xff7a2a, [Canopy.Ice]: 0x7fd6ff, [Canopy.Shock]: 0xf3e65a, [Canopy.Acid]: 0x9be03a,
  [Canopy.Magnetic]: 0xb07aff, [Canopy.Aegis]: PAL.shieldBlue, [Canopy.Coolant]: 0x9fe8ff, [Canopy.Mending]: 0x7be08a,
};

function geo(build: (add: (g: THREE.BufferGeometry, c: number) => void) => void) {
  const list: THREE.BufferGeometry[] = [];
  build((g, c) => list.push(paint(facet(g), c)));
  const m = mergeGeometries(list, false)!;
  for (const g of list) g.dispose();
  return m;
}
/** the ground fireworks read giant on the ground (they're carried a touch smaller) */
const FW_SCALE = 1.5;
/** the deployed sentry: the in-hand toaster at full size, its head about a metre up */
const TURRET_SCALE = 2.4;
const alongZ = (g: THREE.BufferGeometry, z = 0) => { g.rotateX(Math.PI / 2); g.translate(0, 0, z); return g; };

/** elemental barrel drums by Elem (sim/weapons.ts: Fire 0, Acid 1, Shock 2, Ice 3, Magnetic 8) */
const BARREL_COL: Record<number, number> = { 0: 0xff7a2a, 1: 0x7ed957, 2: 0x7fd6ff, 3: 0xbfeeff, 8: 0xe23cff };

export class OrdnanceView {
  private root = new THREE.Group();
  private flying = new Map<number, Flying>();
  private predicted: Flying[] = [];
  private mat = printMaterial({ vertexColors: true, fogMax: 0.35 });
  private ol = outlineMaterial(0.01);
  private geos: Record<string, THREE.BufferGeometry> = {};
  private crates = new Map<number, { g: THREE.Group; gun: THREE.Object3D; beam: THREE.Mesh; x: number; y: number; z: number; wid: number }>();
  private turrets = new Map<number, { g: THREE.Group; head: THREE.Object3D; legs: THREE.Object3D[]; lid: THREE.Object3D; t0: number; until: number; x: number; y: number; z: number }>();
  private lasers: { m: THREE.Mesh; until: number }[] = [];
  private spent: { g: THREE.Group; until: number; y: number }[] = [];
  private domes: { m: THREE.Mesh; until: number; t0: number; canopy: number; r: number }[] = [];
  private beamMat = unlitMaterial({ color: PAL.hazeLight, transparent: true, opacity: 0.35 });
  private hexMat = unlitMaterial({ color: 0x6af05a, transparent: true, opacity: 0.3 });
  private brassMat = unlitMaterial({ color: 0xf0b84a, transparent: true, opacity: 0.28 });
  private ironMat = printMaterial({ color: 0x1a1d1c, fogMax: 0.35 });
  /** the hexed strongbox (sim/game.ts placeStrongbox): one per island; the opener stops seeing theirs */
  private hexBox: { g: THREE.Group; beam: THREE.Mesh; x: number; y: number; z: number } | null = null;
  private laserMat = unlitMaterial({ color: PAL.laserRed, transparent: true, opacity: 0.8 });
  private time = 0;
  private v1 = new THREE.Vector3(); private v2 = new THREE.Vector3(); private v3 = new THREE.Vector3();
  private p = { x: 0, y: 0, z: 0 };

  /** the posse warped to another island */
  setWorld(w: World) { this.world = w; }

  constructor(scene: THREE.Scene, private world: World, private fx: Fx | null) {
    this.root.name = 'ordnance';
    scene.add(this.root);
    (this.beamMat as THREE.MeshBasicMaterial).depthWrite = false;
    (this.laserMat as THREE.MeshBasicMaterial).depthWrite = false;
    this.beamMat.blending = THREE.AdditiveBlending;
    this.laserMat.blending = THREE.AdditiveBlending;
    const G = this.geos;
    G.shell = geo((a) => {
      a(alongZ(new THREE.CylinderGeometry(0.06, 0.06, 0.26, 10)), PAL.toasterRed);
      a(alongZ(new THREE.ConeGeometry(0.062, 0.1, 10), 0.18), PAL.gearCream);
    });
    G.crate = geo((a) => {
      a(new THREE.BoxGeometry(0.9, 0.55, 0.6).translate(0, 0.275, 0), PAL.wood);
      a(new THREE.BoxGeometry(0.92, 0.1, 0.62).translate(0, 0.42, 0), PAL.gunmetal);
      a(new THREE.BoxGeometry(0.3, 0.2, 0.62).translate(0, 0.25, 0.005), PAL.gearTag);
    });
    G.seeker = new THREE.IcosahedronGeometry(0.13, 0);
    G.grenade = new THREE.IcosahedronGeometry(0.11, 0);
  }

  // ------------------------------------------------------------------ flying
  private makeMesh(kind: number, sub: number): THREE.Object3D {
    const G = this.geos;
    const wrap = (m: THREE.Object3D) => { const o = new THREE.Group(); o.add(m); return o; };
    const solid = (g: THREE.BufferGeometry) => { const o = new THREE.Group(); const m = new THREE.Mesh(g, this.mat); m.castShadow = true; o.add(m, new THREE.Mesh(g, this.ol)); return o; };
    switch (kind) {
      case OKind.Grenade: {
        const col = ELEM_COLOR[GRENADES[sub]?.elem ?? -1] ?? PAL.gearTag;
        const m = new THREE.Mesh(G.grenade, new THREE.MeshToonMaterial({ color: PAL.gunmetal, emissive: col, emissiveIntensity: 0.6 }));
        const o = new THREE.Group(); o.add(m, new THREE.Mesh(G.grenade, this.ol)); return o;
      }
      case OKind.Bolt: {
        // the Duel Blaster's plasma: a hot core in a violet shell, drawn additive so it reads at speed
        const o = new THREE.Group();
        o.add(new THREE.Mesh(new THREE.SphereGeometry(0.16, 10, 8), new THREE.MeshBasicMaterial({ color: 0xffffff })));
        o.add(new THREE.Mesh(new THREE.SphereGeometry(0.3, 10, 8), new THREE.MeshBasicMaterial({ color: 0x7a4cff, transparent: true, opacity: 0.55, blending: THREE.AdditiveBlending, depthWrite: false })));
        return o;
      }
      case OKind.Rocket: return wrap(buildOrdnanceMesh('rocket', sub));
      case OKind.BadIdea: { const o = wrap(buildOrdnanceMesh('badidea', sub)); o.scale.setScalar(1.3); return o; }
      case OKind.Harpoon: return wrap(buildOrdnanceMesh('harpoon', sub));
      case OKind.Beacon: { const o = wrap(buildOrdnanceMesh('beacon', sub)); o.scale.setScalar(1.3); return o; }
      case OKind.Shell: {
        // the giant firework, lobbed upright; a canopy mortar's barrel articulates skyward as it falls
        const o = new THREE.Group();
        o.scale.setScalar(FW_SCALE);
        if (weaponDef(sub).payload === Payload.Canopy) {
          o.add(buildOrdnanceMesh('canopyBase', sub));
          const piv = new THREE.Group(); piv.position.y = 0.15; piv.add(buildOrdnanceMesh('canopyTube', sub));
          o.add(piv); o.userData.pivot = piv;
        } else o.add(buildOrdnanceMesh('seekerPlanted', sub));
        return o;
      }
      case OKind.Launch: {
        // the payload going up: the seeker rocket nose-first, or the canopy's paper ball
        const o = new THREE.Group(), inner = new THREE.Group();
        inner.scale.setScalar(FW_SCALE);
        if (weaponDef(sub).payload === Payload.Canopy) inner.add(buildOrdnanceMesh('canopyBall', sub));
        else { const m = buildOrdnanceMesh('seekerPayload', sub); m.position.y = -0.45; const r = new THREE.Group(); r.rotation.x = Math.PI / 2; r.add(m); inner.add(r); }
        o.add(inner);
        return o;
      }
      case OKind.Seeker: {
        const col = ELEM_COLOR[sub] ?? PAL.hazeLight;
        return new THREE.Mesh(G.seeker, unlitMaterial({ color: col }));
      }
    }
    return solid(G.shell);
  }

  /** your own throw / launch, shown on the frame you pressed */
  predict(kind: number, sub: number, owner: number, tick: number, x: number, y: number, z: number, vx: number, vy: number, vz: number) {
    const f = this.add(-1, kind, sub, owner, tick, x, y, z, vx, vy, vz, 0, 0);
    f.predicted = true;
    this.predicted.push(f);
  }

  /** Ev.OrdSpawn: id, kind, sub, tick, x y z, vx vy vz, owner, target */
  spawn(ev: number[], simTick: number) {
    const [id, kind, sub, tick0, x, y, z, vx, vy, vz, owner, target] = ev;
    if (this.flying.has(id)) return;
    // adopt a prediction of the same throw
    for (let i = 0; i < this.predicted.length; i++) {
      const p = this.predicted[i];
      if (p.owner === owner && p.kind === kind && Math.abs(p.tick0 - tick0) <= 3) {
        this.predicted.splice(i, 1);
        p.predicted = false; p.id = id; p.target = target;
        p.tick0 = tick0; p.ox = x; p.oy = y; p.oz = z; p.vx = vx; p.vy = vy; p.vz = vz;
        this.flying.set(id, p);
        return;
      }
    }
    const f = this.add(id, kind, sub, owner, tick0, x, y, z, vx, vy, vz, target, Math.max(0, simTick - tick0));
    this.flying.set(id, f);
  }

  private add(id: number, kind: number, sub: number, owner: number, tick0: number, x: number, y: number, z: number, vx: number, vy: number, vz: number, target: number, catchup: number): Flying {
    const mesh = this.makeMesh(kind, sub);
    mesh.position.set(x, y, z);
    this.root.add(mesh);
    return { id, kind, sub, owner, tick0, target, ox: x, oy: y, oz: z, vx, vy, vz, x, y, z, mesh, shownAt: performance.now(), catchup, predicted: false, dead: false, age: 0,
      rest: false, stick: 0, sx: 0, sy: 0, sz: 0, fuseEnd: 0,
      hx: x, hy: y, hz: z, hvx: vx, hvy: vy, hvz: vz };
  }

  /** Ev.OrdRest: id, x y z, stuck-to eid (0 none), fuse tick */
  rest(ev: number[], simTick: number) {
    const [id, x, y, z, stick, fuseEnd] = ev;
    const f = this.flying.get(id);
    if (!f) return;
    // friendly fire is on: a live grenade on the ground shows its blast ring like a robot bomb
    // (a stuck one rides a robot, so the ring would lie: it gets none)
    if (!stick && f.kind === OKind.Grenade) this.fx?.bombMarker(80000 + id, x, y, z, GRENADES[f.sub]?.radius ?? 5, Math.max(0.1, (fuseEnd - simTick) / TICK_RATE));
    f.rest = true; f.stick = stick; f.fuseEnd = fuseEnd;
    f.x = x; f.y = y; f.z = z;
    f.sx = f.sy = f.sz = 0; // a stuck grenade's offset resolves against the robot on the next frame
    this.fx?.dust(this.v1.set(x, y, z), 0.25);
  }

  /** Ev.OrdEnd: id, x y z, how */
  end(ev: number[]) {
    const [id, x, y, z] = ev;
    const f = this.flying.get(id);
    if (f) { this.kill(f); this.flying.delete(id); }
    // a firework went up: its spent stand (the seeker's stakes, the mortar aimed at the sky) stays a moment
    if (f?.kind === OKind.Shell) {
      const g = new THREE.Group();
      g.position.set(x, y - 0.08, z);
      g.rotation.y = f.mesh.rotation.y;
      g.scale.setScalar(FW_SCALE);
      if (weaponDef(f.sub).payload === Payload.Canopy) {
        g.add(buildOrdnanceMesh('canopyBase', f.sub));
        const piv = new THREE.Group(); piv.position.y = 0.15; piv.rotation.x = -1.25; piv.add(buildOrdnanceMesh('canopyBarrel', f.sub));
        g.add(piv);
      } else g.add(buildOrdnanceMesh('seekerStand', f.sub));
      this.root.add(g);
      this.spent.push({ g, until: this.time + 3.2, y: y - 0.08 });
      this.fx?.dust(this.v1.set(x, y, z), 0.7);
    }
    // the Big One's beacon: three seconds of red laser, then the sky falls
    if (f?.kind === OKind.Beacon) {
      const m = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.12, 120, 8, 1, true), this.laserMat);
      m.position.set(x, y + 60, z);
      this.root.add(m);
      this.lasers.push({ m, until: this.time + BEACON_SECS + 0.2 });
      this.fx?.bombMarker(70000 + id, x, y, z, weaponDef(f.sub).blast, BEACON_SECS);
    }
  }

  private kill(f: Flying) {
    f.dead = true;
    f.mesh.removeFromParent();
    f.mesh.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh && !m.userData.shared && m.material !== this.mat && m.material !== this.ol) (m.material as THREE.Material).dispose(); });
  }

  // ------------------------------------------------------------------ crates
  /** Ev.Crate: id, weapon, x y z, alive, taker */
  crate(ev: number[]) {
    const [id, wid, x, y, z, alive] = ev;
    const c = this.crates.get(id);
    if (!alive) {
      if (c) { c.g.removeFromParent(); this.crates.delete(id); this.fx?.dust(this.v1.set(x, y, z), 0.6); }
      return;
    }
    if (c || !WEAPONS[wid]) return;
    const g = new THREE.Group();
    g.position.set(x, y, z);
    const box = new THREE.Mesh(this.geos.crate, this.mat); box.castShadow = true;
    g.add(box, new THREE.Mesh(this.geos.crate, this.ol));
    const gun = buildWeaponMesh(wid);
    gun.position.set(0, 1.1, 0);
    gun.scale.setScalar(1.6);
    g.add(gun);
    const beam = new THREE.Mesh(new THREE.CylinderGeometry(0.25, 0.4, 14, 10, 1, true), this.beamMat);
    beam.position.y = 7;
    g.add(beam);
    this.root.add(g);
    this.crates.set(id, { g, gun, beam, x, y, z, wid });
  }
  /** Ev.Strongbox: x y z, state (1 placed / 0 opened), who opened it */
  strongbox(ev: number[], myId: number) {
    const [x, y, z, state, who] = ev;
    if (state >= 2) { this.cache(x, y, z, state === 3, who, myId); return; } // 3 a munitions cache placed, 2 claimed
    if (!state) { if (who === myId && this.hexBox) { this.hexBox.g.removeFromParent(); this.hexBox = null; this.fx?.dust(this.v1.set(x, y + 0.5, z), 1.2); } return; }
    if (this.hexBox) { this.hexBox.g.removeFromParent(); this.hexBox = null; }
    const g = new THREE.Group();
    g.position.set(x, y, z);
    const box = new THREE.Mesh(this.geos.crate, this.ironMat); box.castShadow = true; box.scale.set(1.1, 0.8, 0.8);
    const ol = new THREE.Mesh(this.geos.crate, this.ol); ol.scale.copy(box.scale);
    g.add(box, ol);
    const beam = new THREE.Mesh(new THREE.CylinderGeometry(0.3, 0.55, 10, 10, 1, true), this.hexMat);
    beam.position.y = 5;
    g.add(beam);
    this.root.add(g);
    this.hexBox = { g, beam, x, y, z };
  }
  /** the munitions cache (sim/game.ts placeCache): a brass-banded crate; gone for you once you claimed it */
  private cacheBox: { g: THREE.Group; beam: THREE.Mesh } | null = null;
  private cache(x: number, y: number, z: number, placed: boolean, who: number, myId: number) {
    if (!placed) { if (who === myId && this.cacheBox) { this.cacheBox.g.removeFromParent(); this.cacheBox = null; } return; }
    if (this.cacheBox) { this.cacheBox.g.removeFromParent(); this.cacheBox = null; }
    const g = new THREE.Group();
    g.position.set(x, y, z);
    const box = new THREE.Mesh(this.geos.crate, this.mat); box.castShadow = true; box.scale.set(0.9, 0.7, 0.9);
    const ol = new THREE.Mesh(this.geos.crate, this.ol); ol.scale.copy(box.scale);
    g.add(box, ol);
    const beam = new THREE.Mesh(new THREE.CylinderGeometry(0.25, 0.45, 8, 10, 1, true), this.brassMat);
    beam.position.y = 4;
    g.add(beam);
    this.root.add(g);
    this.cacheBox = { g, beam };
  }
  /** Ev.Barrel (sim/game.ts placeBarrels): id, element + 1, x y z, state, cluster level. An oil drum in its element's colour; a
   *  band per level; gone when it goes off (the blast draws itself) */
  private barrels = new Map<number, THREE.Group>();
  barrel(ev: number[]) {
    const [id, el1, x, y, z, state, level] = ev;
    const old = this.barrels.get(id);
    if (!state) { if (old) { old.removeFromParent(); this.barrels.delete(id); } return; }
    if (old) return;
    const col = BARREL_COL[el1 - 1] ?? 0x888888;
    const g = new THREE.Group();
    g.position.set(x, y, z);
    const drum = new THREE.Mesh(new THREE.CylinderGeometry(0.5, 0.5, 1.0, 12), new THREE.MeshToonMaterial({ color: col, emissive: col, emissiveIntensity: 0.25 }));
    drum.position.y = 0.5; drum.castShadow = true;
    g.add(drum, new THREE.Mesh(drum.geometry, this.ol).translateY(0.5));
    for (let i = 0; i < Math.min(3, level); i++) { const band = new THREE.Mesh(new THREE.TorusGeometry(0.52, 0.035, 6, 16), this.mat); band.rotation.x = Math.PI / 2; band.position.y = 0.2 + i * 0.3; g.add(band); }
    this.root.add(g);
    this.barrels.set(id, g);
  }
  /** the strongbox in reach of a point (HUD prompt) */
  strongboxNear(x: number, z: number, r: number) { const b = this.hexBox; return !!b && Math.hypot(b.x - x, b.z - z) < r; }
  /** crates within range of a point (HUD prompt) */
  nearestCrate(x: number, z: number, r: number) {
    let best: { wid: number; d: number } | null = null;
    for (const c of this.crates.values()) {
      const d = Math.hypot(c.x - x, c.z - z);
      if (d < r && (!best || d < best.d)) best = { wid: c.wid, d };
    }
    return best;
  }
  crateList() { return this.crates.values(); }

  // ------------------------------------------------------------------ turrets
  /** Ev.Turret: id, x y z, ticks, owner */
  turret(ev: number[]) {
    const [id, x, y, z, ticks] = ev;
    if (this.turrets.has(id)) return;
    // the toaster sentry lands folded, kicks its four legs out, rises, and pops its lid
    const g = new THREE.Group();
    g.position.set(x, y, z);
    g.scale.setScalar(TURRET_SCALE);
    const legs: THREE.Object3D[] = [];
    for (let i = 0; i < 4; i++) {
      const l = new THREE.Group();
      l.rotation.y = Math.PI / 4 + (i * Math.PI) / 2;
      l.add(buildOrdnanceMesh('turretLeg', W.Turret));
      g.add(l); legs.push(l);
    }
    const head = new THREE.Group();
    head.add(buildOrdnanceMesh('turretBody', W.Turret));
    const lid = new THREE.Group(); lid.position.set(0, 0.17, -0.115);
    lid.add(buildOrdnanceMesh('turretLid', W.Turret));
    head.add(lid);
    g.add(head);
    this.root.add(g);
    this.turrets.set(id, { g, head, legs, lid, t0: this.time, until: this.time + ticks / TICK_RATE, x, y, z });
    this.deploy(this.turrets.get(id)!);
    this.fx?.dust(this.v1.set(x, y, z), 0.5);
  }
  private deploy(t: { head: THREE.Object3D; legs: THREE.Object3D[]; lid: THREE.Object3D; t0: number }) {
    const k = Math.min(1, Math.max(0, (this.time - t.t0) / 0.35));
    const up = Math.min(1, Math.max(0, (this.time - t.t0 - 0.2) / 0.3));
    const pop = Math.min(1, Math.max(0, (this.time - t.t0 - 0.45) / 0.2));
    const e = k * k * (3 - 2 * k), eu = up * up * (3 - 2 * up);
    for (const l of t.legs) { l.scale.set(1, 0.25 + 0.75 * e, 0.3 + 0.7 * e); l.position.y = 0.35 * eu; }
    t.head.position.y = 0.06 + 0.32 * eu - 0.09 * (1 - eu);
    t.lid.rotation.x = -1.1 * pop + Math.sin(pop * Math.PI) * -0.3;
  }
  /** Ev.TurretShot: id, dir xyz, dist, kind */
  /** where a sentry stands (for its sounds) */
  turretPos(id: number) { const t = this.turrets.get(id); return t ? { x: t.x, y: t.y + 1, z: t.z } : undefined; }

  turretShot(ev: number[]) {
    const [id, dx, dy, dz, dist, kind] = ev;
    const t = this.turrets.get(id);
    if (!t) return;
    if (kind === 255) { t.g.removeFromParent(); this.turrets.delete(id); this.fx?.explosion(this.v1.set(t.x, t.y + 0.8, t.z), 1.2, 0); return; }
    t.head.rotation.y = Math.atan2(dx, dz);
    const from = this.v1.set(t.x + dx * 0.55, t.y + 1.0 + dy * 0.55, t.z + dz * 0.55);
    const to = this.v2.set(t.x + dx * dist, t.y + 1.0 + dy * dist, t.z + dz * dist);
    this.fx?.tracer(from, to, 3, false);
    this.fx?.muzzle(from, this.v3.set(dx, dy, dz), 1, false);
  }

  // ------------------------------------------------------------------ canopies
  /** canopy hazard (Ev.Hazard element >= CANOPY_EL): the footprint via fx, plus a dome */
  canopy(id: number, canopy: number, x: number, y: number, z: number, r: number, seconds: number) {
    const fxEl = CANOPY_FX[canopy] ?? -1;
    if (fxEl >= 0) this.fx?.hazard(id, fxEl, x, y, z, r, seconds);
    const mat = new THREE.MeshBasicMaterial({ color: CANOPY_COLOR[canopy] ?? PAL.hazeLight, transparent: true, opacity: canopy === Canopy.Aegis ? 0.22 : 0.12, depthWrite: false, side: THREE.DoubleSide });
    mat.blending = THREE.AdditiveBlending;
    const m = new THREE.Mesh(new THREE.SphereGeometry(r, 24, 12, 0, Math.PI * 2, 0, Math.PI / 2), mat);
    m.position.set(x, y, z);
    this.root.add(m);
    this.domes.push({ m, until: this.time + seconds, t0: this.time, canopy, r });
  }

  // ------------------------------------------------------------------ per frame
  update(dt: number, displayTick: (f: { tick0: number; catchup: number; shownAt: number }) => number, enemies: NetEnemy[], enemyCount: number) {
    this.time += dt;
    const fx = this.fx;
    for (const f of this.flying.values()) this.fly(f, dt, displayTick, enemies, enemyCount);
    for (let i = this.predicted.length - 1; i >= 0; i--) {
      const f = this.predicted[i];
      this.fly(f, dt, displayTick, enemies, enemyCount);
      if (f.age > 1.5 || f.dead) { if (!f.dead) this.kill(f); this.predicted.splice(i, 1); } // never confirmed
    }
    for (const [id, f] of this.flying) if (f.dead) this.flying.delete(id);
    // crates bob and spin; the gun floats over them
    for (const c of this.crates.values()) {
      c.gun.rotation.y = this.time * 1.4;
      c.gun.position.y = 1.1 + Math.sin(this.time * 2.2 + c.x) * 0.08;
      (c.beam.material as THREE.MeshBasicMaterial).opacity = 0.25 + 0.1 * Math.sin(this.time * 3);
    }
    if (this.hexBox) {
      const b = this.hexBox;
      (b.beam.material as THREE.MeshBasicMaterial).opacity = 0.2 + 0.12 * Math.sin(this.time * 1.7);
      if (fx && Math.random() < dt * 2) fx.dust(this.v1.set(b.x + (Math.random() - 0.5) * 0.4, b.y + 0.7, b.z + (Math.random() - 0.5) * 0.4), 0.5); // the smoking tell
    }
    for (let i = this.spent.length - 1; i >= 0; i--) {
      const p = this.spent[i], left = p.until - this.time;
      p.g.position.y = p.y - Math.max(0, 0.6 - left) * 0.8; // settles into the ground at the end
      if (fx && left > 1.2 && Math.random() < dt * 8) fx.dust(this.v1.set(p.g.position.x, p.g.position.y + 0.4, p.g.position.z), 0.12);
      if (left < 0) { p.g.removeFromParent(); this.spent.splice(i, 1); }
    }
    for (const t of this.turrets.values()) this.deploy(t);
    for (const [id, t] of this.turrets) if (this.time > t.until + 1) { t.g.removeFromParent(); this.turrets.delete(id); }
    for (let i = this.lasers.length - 1; i >= 0; i--) {
      const l = this.lasers[i];
      l.m.scale.x = l.m.scale.z = 0.6 + 0.4 * Math.sin(this.time * 30);
      if (this.time > l.until) { l.m.removeFromParent(); l.m.geometry.dispose(); this.lasers.splice(i, 1); }
    }
    for (let i = this.domes.length - 1; i >= 0; i--) {
      const d = this.domes[i];
      const k = Math.min(1, (this.time - d.t0) / 0.35), out = Math.min(1, Math.max(0, d.until - this.time) / 0.6);
      d.m.scale.setScalar(Math.max(0.01, k * (0.98 + 0.02 * Math.sin(this.time * 4))));
      (d.m.material as THREE.MeshBasicMaterial).opacity = (d.canopy === Canopy.Aegis ? 0.22 : 0.12) * out;
      // a little rain from the canopy
      if (fx && Math.random() < dt * 14) {
        const a = Math.random() * 6.283, rr = Math.sqrt(Math.random()) * d.r * 0.9;
        const el = CANOPY_FX[d.canopy];
        this.v1.set(d.m.position.x + Math.cos(a) * rr, d.m.position.y + 0.3, d.m.position.z + Math.sin(a) * rr);
        if (el !== undefined && el >= 0) fx.element(this.v1, el, 0.35);
        else fx.dust(this.v1, 0.15);
      }
      if (this.time > d.until) { d.m.removeFromParent(); d.m.geometry.dispose(); (d.m.material as THREE.Material).dispose(); this.domes.splice(i, 1); }
    }
  }

  private fly(f: Flying, dt: number, displayTick: (f: { tick0: number; catchup: number; shownAt: number }) => number, enemies: NetEnemy[], n: number) {
    if (f.dead) return;
    f.age += dt;
    const def = ORD[f.kind];
    let x: number, y: number, z: number;
    const fused = f.kind === OKind.Grenade && (GRENADES[f.sub]?.fuse ?? 0) > 0;
    if (f.rest) {
      // settled: sit (or ride the robot it stuck to), the light blinking faster as the fuse runs out
      x = f.x; y = f.y; z = f.z;
      if (f.stick) {
        for (let i = 0; i < n; i++) if (enemies[i].id === f.stick) {
          const e = enemies[i];
          if (!f.sx && !f.sy && !f.sz) { f.sx = f.x - e.x; f.sy = f.y - e.y; f.sz = f.z - e.z; }
          x = e.x + f.sx; y = e.y + f.sy; z = e.z + f.sz; break;
        }
      }
      f.x = x; f.y = y; f.z = z;
      f.mesh.position.set(x, y, z);
      const left = f.fuseEnd > 0 ? (f.fuseEnd - displayTick(f)) / TICK_RATE : 1;
      const rate = left < 0.6 ? 16 : left < 1.4 ? 8 : 3;
      const lit = Math.sin(this.time * rate * Math.PI) > 0;
      const m = (f.mesh.children[0] as THREE.Mesh).material as THREE.MeshToonMaterial;
      if (m?.emissiveIntensity !== undefined) m.emissiveIntensity = lit ? 2.2 : 0.3;
      if (f.age > 12) this.kill(f); // never heard the end
      return;
    }
    if (def.homing > 0) {
      // the server steers for real; we steer the same way toward where we see the target
      // the Duel Blaster's bolt has its own speed (the gun's projSpeed is 0: a divide by it made every locked bolt NaN, invisible)
      const speed = f.kind === OKind.Seeker ? SEEKER_SPEED : f.kind === OKind.Bolt ? DUEL_BOLT_SPEED : weaponDef(f.sub).projSpeed || 1;
      let t: NetEnemy | null = null;
      for (let i = 0; i < n; i++) if (enemies[i].id === f.target) { t = enemies[i]; break; }
      if (t) {
        let dx = t.x - f.hx, dy = t.y + ENEMIES[t.type].bodyY - f.hy, dz = t.z - f.hz;
        const dl = Math.hypot(dx, dy, dz) || 1; dx /= dl; dy /= dl; dz /= dl;
        const k = Math.min(1, def.homing * dt);
        let nx = f.hvx / speed + (dx - f.hvx / speed) * k, ny = f.hvy / speed + (dy - f.hvy / speed) * k, nz = f.hvz / speed + (dz - f.hvz / speed) * k;
        const nl = Math.hypot(nx, ny, nz) || 1;
        f.hvx = nx / nl * speed; f.hvy = ny / nl * speed; f.hvz = nz / nl * speed;
      }
      f.hx += f.hvx * dt; f.hy += f.hvy * dt; f.hz += f.hvz * dt;
      x = f.hx; y = f.hy; z = f.hz;
      if (f.age > def.life + 0.5) { this.kill(f); return; }
    } else {
      const tk = displayTick(f);
      ordPosAt(f.kind, f.tick0, f.ox, f.oy, f.oz, f.vx, f.vy, f.vz, tk, this.p);
      x = this.p.x; y = this.p.y; z = this.p.z;
      // stop at the ground on our side too; the server's end event removes it for good
      const g = this.world.support(x, z, y + 0.5);
      if (f.kind !== OKind.Launch && g > -1e6 && y < g + 0.08) {
        y = g + 0.08;
        // a fused grenade stops where it lands; the server's rest event confirms the spot
        if (fused) { f.rest = true; f.x = x; f.y = y; f.z = z; f.mesh.position.set(x, y, z); return; }
      }
      if (f.kind === OKind.Launch && (tk - f.tick0) / TICK_RATE > def.life + 0.2) { this.kill(f); return; }
      if (!fused && (tk - f.tick0) / TICK_RATE > def.life + 0.5) { this.kill(f); return; }
    }
    const vx = x - f.x, vy = y - f.y, vz = z - f.z;
    f.x = x; f.y = y; f.z = z;
    const m = f.mesh;
    m.position.set(x, y, z);
    const sp = Math.hypot(vx, vy, vz);
    if (f.kind === OKind.Grenade || f.kind === OKind.Beacon) { m.rotation.x += dt * 9; m.rotation.z += dt * 5; }
    else if (f.kind === OKind.Shell) {
      // fireworks fly upright, facing where they were thrown; the mortar tilts its barrel up on the way down
      if (Math.abs(vx) + Math.abs(vz) > 1e-4) m.rotation.y = Math.atan2(vx, vz);
      const piv = m.userData.pivot as THREE.Object3D | undefined;
      if (piv) piv.rotation.x = -1.25 * Math.min(1, Math.max(0, (f.age - 0.2) / 0.45));
    }
    else if (sp > 1e-4) {
      this.v1.set(x + vx, y + vy, z + vz);
      m.lookAt(this.v1);
    }
    const fx = this.fx;
    if (!fx) return;
    if (f.kind === OKind.Rocket || f.kind === OKind.BadIdea || f.kind === OKind.Launch || f.kind === OKind.Seeker) {
      const l = sp || 1;
      this.v2.set(-vx / l, -vy / l, -vz / l);
      if (f.kind === OKind.Launch) this.v2.set(0, -1, 0);
      const back = f.kind === OKind.BadIdea ? 0.6 : 0.35;
      this.v1.set(x + this.v2.x * back, y + this.v2.y * back, z + this.v2.z * back);
      fx.jetFlame(this.v1, this.v2, f.kind === OKind.Seeker ? 0.35 : f.kind === OKind.BadIdea ? 1.5 : 1);
      if (f.kind === OKind.Seeker && Math.random() < 0.3) fx.element(this.v1, ELEM_FX[f.sub] ?? El.Fire, 0.2);
    }
    void LAUNCH_SPEED;
  }

  /** a robot caught an elemental proc */
  proc(pos: THREE.Vector3, elem: number) {
    const el = ELEM_FX[elem];
    if (el === undefined || !this.fx) return;
    if (elem === 8) this.fx.chainArc(this.v1.copy(pos).add(this.v2.set(0, 0.8, 0)), pos, El.Shock);
    else this.fx.element(pos, el, 0.8);
  }

  reset() {
    for (const f of this.flying.values()) this.kill(f);
    for (const f of this.predicted) this.kill(f);
    this.flying.clear(); this.predicted.length = 0;
    if (this.hexBox) { this.hexBox.g.removeFromParent(); this.hexBox = null; }
    if (this.cacheBox) { this.cacheBox.g.removeFromParent(); this.cacheBox = null; }
    for (const b of this.barrels.values()) b.removeFromParent(); this.barrels.clear();
    for (const c of this.crates.values()) c.g.removeFromParent();
    this.crates.clear();
  }

  dispose() {
    this.reset();
    this.root.removeFromParent();
  }
}
